import React from 'react';
import { motion } from 'framer-motion';
import ModernHeader from '../../components/modern/ModernHeader';
import KPIGrid from '../../components/modern/KPIGrid';
import Timeline from '../../components/modern/Timeline';
import ModernFooter from '../../components/modern/ModernFooter';
import SEO from '../../components/modern/SEO';

export default function ModernAboutPage() {
  return (
    <>
      <SEO 
        title="Rólunk - XL bútor | 35+ év tapasztalat egyedi bútor gyártásban"
        description="Vállalkozásunkat 1989-ben alapítottuk. Ma 1500 m² műhelyben, 3D tervezéssel és saját asztalos műhellyel készítjük egyedi bútorainkat Kisvárda területén."
      />
      
      <div className="bg-[var(--color-bg)]">
        <ModernHeader />
        
        <main>
          {/* Hero Section */}
          <section className="relative py-4 sm:py-0">
            <div className="mx-auto w-full max-w-[var(--container-max)] px-4">
              <div className="relative overflow-hidden rounded-lg sm:rounded-xl">
                <img
                  src="/images/Weboldalképei/513502099_23889229397400221_4984633965961444247_n.jpg"
                  alt="XL bútor műhely"
                  className="w-full h-[40vh] sm:h-[45vh] md:h-[55vh] lg:h-[60vh] object-cover"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
                
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="absolute left-4 right-4 sm:left-6 sm:right-auto bottom-4 sm:bottom-6 bg-white/95 backdrop-blur-sm px-6 py-6 sm:px-8 sm:py-8 max-w-full sm:max-w-2xl rounded-2xl shadow-xl"
                >
                  <div className="text-sm font-semibold text-[var(--color-accent)] uppercase tracking-wider mb-3">
                    Az XL bútor története
                  </div>
                  <h1 className="font-[var(--font-serif)] text-[28px] sm:text-[36px] md:text-[44px] font-semibold leading-tight text-gray-900 mb-3">
                    Egyedi bútorok, professzionális kivitelezés – 1989 óta
                  </h1>
                  <p className="text-[14px] sm:text-[15px] text-[var(--color-text-secondary)] leading-relaxed">
                    Több mint három évtizedes tapasztalattal készítjük egyedi bútoraidat saját asztalos műhelyünkben.
                  </p>
                </motion.div>
              </div>
            </div>
          </section>

          {/* KPI Grid */}
          <KPIGrid />

          {/* Mission Section */}
          <section className="py-16 md:py-24 bg-white">
            <div className="mx-auto max-w-[var(--container-max)] px-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-16 items-center">
                <div>
                  <div className="text-sm font-semibold text-[var(--color-accent)] uppercase tracking-wider mb-3">
                    Küldetésünk
                  </div>
                  <h2 className="text-3xl md:text-4xl font-[var(--font-serif)] font-semibold text-[var(--color-text-primary)] tracking-tight mb-6">
                    Egyedi bútorok az Ön elképzelése szerint
                  </h2>
                  <div className="space-y-4 text-[var(--color-text-secondary)] leading-relaxed">
                    <p>
                      Fő tevékenységünk az egyedi bútor gyártás, amely a megrendelőink maximális 
                      testreszabási igényei szerint készül. 3D tervezéssel kezdjük, majd saját 
                      asztalos műhelyünkben készítjük el mindent.
                    </p>
                    <p>
                      A tervezéstől a kivitelezésig minden egy helyen történik. Ezen kívül széles 
                      választékban kínálunk szőnyegeket, lakberendezési eszközöket és konyhafelszerelési 
                      tárgyakat is.
                    </p>
                  </div>
                </div>

                <div>
                  <img 
                    src="/images/Weboldalképei/513502099_23889229397400221_4984633965961444247_n.jpg"
                    alt="XL bútor műhely" 
                    className="w-full aspect-[4/3] object-cover rounded-2xl shadow-[var(--shadow-md)]" 
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Timeline */}
          <Timeline />

          {/* Services Grid */}
          <section className="py-16 md:py-24 bg-white">
            <div className="mx-auto max-w-[var(--container-max)] px-4">
              <div className="text-center mb-12 md:mb-16">
                <div className="text-sm font-semibold text-[var(--color-accent)] uppercase tracking-wider mb-3">
                  Szolgáltatásaink
                </div>
                <h2 className="text-3xl md:text-4xl font-[var(--font-serif)] font-semibold text-[var(--color-text-primary)] tracking-tight">
                  Amit biztosan kapsz tőlünk
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                {[
                  {
                    icon: '🛠️',
                    title: 'Egyedi bútor gyártás',
                    text: '3D tervezés és teljes körű kivitelezés saját műhelyünkben, maximális testreszabással.'
                  },
                  {
                    icon: '🏪',
                    title: 'Széles választék',
                    text: 'Új bútor, szőnyeg, függöny, lakberendezési és konyhafelszerelési eszközök egy helyen.'
                  },
                  {
                    icon: '🚚',
                    title: 'Szállítás és költöztetés',
                    text: 'Bútorszállítás, költöztetés és fuvarozás is szolgáltatásaink között.'
                  },
                  {
                    icon: '📐',
                    title: 'Lapszabászat',
                    text: 'Modern lapszabászat és bútorlap raktár a gyors és precíz munkavégzésért.'
                  }
                ].map((service, index) => (
                  <div
                    key={index}
                    className="bg-[var(--color-bg)] rounded-2xl p-6 md:p-8 text-center shadow-[var(--shadow-subtle)] md:hover:shadow-[var(--shadow-md)] transition-all duration-200 md:hover:-translate-y-1"
                  >
                    <div className="text-5xl mb-4">{service.icon}</div>
                    <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-3">
                      {service.title}
                    </h3>
                    <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">
                      {service.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </main>

        <ModernFooter />
      </div>
    </>
  );
}

